<script>
import { MODAL, OPCOES_FILTRO_DATA } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import { resolveWalletScope } from '@modules/consolidator/utils/wallet-scope/wallet-scope'
import { apiConsolidatorWalletAnalysis } from '@modules/consolidator/api/apiConsolidatorWalletAnalysis'
import {
  buildWalletOptionsFields,
  filterOptionsFieldsForProfile,
} from '@modules/consolidator/utils/build-wallet-options-fields/build-wallet-options-fields'
import { formatDateFilterLabel } from '@modules/consolidator/utils/format-date-filter-label/format-date-filter-label'
import {
  FIELD_KEY_TO_ACTION,
  FIELD_KEY_TO_GETTER,
  WALLET_FIELD_DEFAULTS,
  WALLET_STANDALONE_FILTER_KEYS,
  applyWalletFilterDefaults,
  hasAsyncItems,
  resolveWalletFilterFields,
  restoreIncompleteSpecificDate,
} from '@modules/consolidator/utils/normalize-wallet-filters/normalize-wallet-filters'
import { getWalletModoLabel } from '@modules/consolidator/utils/wallet-modo-configs/wallet-modo-configs'
import { buildWalletCombinedFields } from '@modules/consolidator/utils/wallet-active-count/wallet-active-count'
import { buildFilterDefaultsSignature } from '@modules/consolidator/utils/wallet-filter-signature/wallet-filter-signature'
import WalletCustomDate from '@modules/consolidator/modal/wallet-custom-date/wallet-custom-date.vue'

export default {
  name: 'ConsolidatorWalletFilter',
  components: { WalletCustomDate },
  props: {
    fields: {
      type: Array,
      default: null,
    },
    enabledFieldKeys: {
      type: Array,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    deferApply: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectRemountKey: 0,
      fieldKeyToGetter: FIELD_KEY_TO_GETTER,
      fieldKeyToAction: FIELD_KEY_TO_ACTION,
      localDraft: null,
    }
  },
  computed: {
    clienteAtual() {
      return this.$store.getters['user/currentClient'] || {}
    },
    idCliente() {
      return this.clienteAtual.id || ''
    },
    currentModo() {
      return this.$store.getters['consolidator/walletFiltersModo']
    },
    draftModo() {
      if (this.deferApply && this.localDraft) {
        return this.localDraft.modoFiltroCorretora
      }
      return this.$store.getters['consolidator/walletFiltersModo']
    },
    effectiveStore() {
      if (!this.deferApply) return this.$store
      return this.buildDraftStore()
    },
    corretoraDynamicLabel() {
      return getWalletModoLabel(this.draftModo) || 'Instituição'
    },
    walletAnalysisOptionsFields() {
      return this.$store.getters['consolidator/walletAnalysisOptionsFields']
    },
    walletAnalysisOptionsFieldsLoading() {
      return this.$store.getters[
        'consolidator/walletAnalysisOptionsFieldsLoading'
      ]
    },
    combinedFields() {
      return buildWalletCombinedFields({
        fields: Array.isArray(this.fields) ? this.fields : this.defaultFields,
        dynamicFields: this.walletAnalysisOptionsFields,
        enabledFieldKeys: this.enabledFieldKeys,
        groupedModo: this.draftModo,
      })
    },
    defaultFields() {
      return [
        { key: 'modoFiltroCorretora' },
        { key: 'filtroCorretora' },
        { key: 'investmentTypeFilter' },
        {
          key: 'filtroData',
          items: Object.values(OPCOES_FILTRO_DATA),
        },
      ]
    },
    resolvedFields() {
      return resolveWalletFilterFields({
        fields: this.combinedFields,
        store: this.effectiveStore,
        dynamicLabels: { filtroCorretora: this.corretoraDynamicLabel },
      })
    },
    filterDefaultsSignature() {
      return buildFilterDefaultsSignature({
        fields: this.resolvedFields,
        groupedModo: this.draftModo,
      })
    },
  },
  watch: {
    localDraft: {
      handler(draft) {
        if (!this.deferApply) return
        this.$emit('draft-change', draft ? { ...draft } : null)
      },
      deep: true,
      immediate: true,
    },
    filterDefaultsSignature() {
      this.applyDefaults()
    },
    idCliente() {
      this.resetCorretoraSelection()
      this.loadFilterOptions()
    },
    currentModo() {
      if (this.deferApply) return
      this.resetCorretoraSelection()
    },
  },
  created() {
    this.ensureWalletAnalysisOptionsFieldsPlaceholders()
    this.applyDefaults()
  },
  methods: {
    ensureWalletAnalysisOptionsFieldsPlaceholders() {
      if (this.walletAnalysisOptionsFields.length > 0) return
      this.$store.dispatch(
        'consolidator/setWalletAnalysisOptionsFields',
        filterOptionsFieldsForProfile(buildWalletOptionsFields(null), {
          isClient: this.$currentUser().IS_CLIENTE,
        })
      )
    },
    loadFilterOptions() {
      if (!resolveWalletScope({ idCliente: this.idCliente }).ativo) return
      apiConsolidatorWalletAnalysis.loadOptions({
        id_cliente_app: this.idCliente,
        is_client: this.$currentUser().IS_CLIENTE,
      })
    },
    resetCorretoraSelection() {
      const field = this.resolvedFields.find(
        (item) => item.key === 'filtroCorretora'
      )
      if (!field || field.default === undefined) return
      this.$store.dispatch(
        'consolidator/setWalletFiltersCorretora',
        field.default
      )
      this.$store.dispatch('consolidator/setWalletFiltersUserChangedField', {
        key: 'filtroCorretora',
        value: false,
      })
      apiConsolidatorWalletAnalysis.loadOptions({
        id_cliente_app: this.idCliente,
        is_client: this.$currentUser().IS_CLIENTE,
      })
    },
    buildDraftStore() {
      const realGetters = this.$store.getters
      const draftModo = this.draftModo
      return {
        getters: new Proxy(realGetters, {
          get(target, prop) {
            if (prop === 'consolidator/walletFiltersModo') {
              return draftModo
            }
            return target[prop]
          },
        }),
      }
    },
    seedDraftFromStore() {
      const draft = {}
      const keys = this.combinedFields.map((field) =>
        typeof field === 'string' ? field : field.key
      )
      for (const key of keys) {
        draft[key] = this.readStoreValue(key)
      }
      draft.dataInicio =
        this.$store.getters['consolidator/walletFiltersDataInicio']
      draft.dataFim = this.$store.getters['consolidator/walletFiltersDataFim']
      this.localDraft = draft
    },
    ensureDraftKeys() {
      if (!this.localDraft) {
        this.seedDraftFromStore()
        return
      }
      const keys = this.combinedFields.map((field) =>
        typeof field === 'string' ? field : field.key
      )
      for (const key of keys) {
        if (!(key in this.localDraft)) {
          this.$set(this.localDraft, key, this.readStoreValue(key))
        }
      }
    },
    readStoreValue(key) {
      const getter = this.fieldKeyToGetter[key]
      return getter ? this.$store.getters[getter] : ''
    },
    resetDraft() {
      this.seedDraftFromStore()
    },
    // Fecha e commita qualquer campo multi-select ainda com o dropdown aberto
    // (ex: Portfólio) — chamado pelo WalletFilters ANTES de applyDraft(), pra
    // não perder uma seleção feita sem fechar o campo antes de clicar Aplicar.
    blurOpenFields() {
      const inputs = this.$refs.fieldInputs
      if (!Array.isArray(inputs)) return
      inputs.forEach((input) => {
        if (input && typeof input.blur === 'function') input.blur()
      })
    },
    clearToDefaults() {
      if (this.deferApply) {
        this.clearLocalDraftToDefaults()
        return
      }
      this.clearToDefaultsImmediately()
    },
    clearToDefaultsImmediately() {
      for (const key of Object.keys(this.fieldKeyToAction)) {
        const action = this.fieldKeyToAction[key]
        const config = WALLET_FIELD_DEFAULTS[key]
        const defaultValue =
          config && config.default !== undefined ? config.default : []
        if (
          key === 'filtroData' &&
          defaultValue !== OPCOES_FILTRO_DATA.ESPECIFICA
        ) {
          this.$store.dispatch(
            'consolidator/setWalletFiltersLastValidData',
            defaultValue
          )
        }
        this.$store.dispatch(action, defaultValue)
        this.$store.dispatch('consolidator/setWalletFiltersUserChangedField', {
          key,
          value: false,
        })
      }
      this.$store.dispatch('consolidator/setWalletFiltersDataInicio', null)
      this.$store.dispatch('consolidator/setWalletFiltersDataFim', null)
      this.seedDraftFromStore()
      this.emitFilter()
    },
    // Modo deferApply: "Limpar" só reseta o que é exibido no popover — nada é
    // gravado na store nem recarregado aqui. A aplicação real (dispatch +
    // reload) só ocorre em applyDraft(), disparado pelo BaseFilterMenu ao
    // clicar "Aplicar" ou ao fechar o popover clicando fora dele.
    clearLocalDraftToDefaults() {
      const cleared = {}
      for (const key of Object.keys(this.fieldKeyToAction)) {
        const config = WALLET_FIELD_DEFAULTS[key]
        cleared[key] =
          config && config.default !== undefined ? config.default : []
      }
      cleared.dataInicio = null
      cleared.dataFim = null
      this.localDraft = cleared
    },
    applyDraft() {
      if (!this.localDraft) return
      const draft = this.localDraft
      if ('dataInicio' in draft) {
        this.$store.dispatch(
          'consolidator/setWalletFiltersDataInicio',
          draft.dataInicio
        )
      }
      if ('dataFim' in draft) {
        this.$store.dispatch(
          'consolidator/setWalletFiltersDataFim',
          draft.dataFim
        )
      }
      for (const key of Object.keys(this.fieldKeyToAction)) {
        if (!(key in draft)) continue
        this.commitFieldToStore(key, draft[key])
      }
      this.emitFilter()
    },
    getDateValue(key) {
      if (this.deferApply && this.localDraft && key in this.localDraft) {
        return this.localDraft[key]
      }
      const getterByKey = {
        dataInicio: 'consolidator/walletFiltersDataInicio',
        dataFim: 'consolidator/walletFiltersDataFim',
      }
      return this.$store.getters[getterByKey[key]]
    },
    getFieldValue(key) {
      if (this.deferApply && this.localDraft && key in this.localDraft) {
        return this.localDraft[key]
      }
      const getter = this.fieldKeyToGetter[key]
      if (getter) return this.$store.getters[getter]
      const field = this.resolvedFields.find((f) => f.key === key)
      return field && field.multiple ? [] : ''
    },
    setDraftValue(key, value) {
      if (!this.localDraft) this.seedDraftFromStore()
      this.$set(this.localDraft, key, value)
    },
    commitFieldToStore(key, value) {
      const action = this.fieldKeyToAction[key]
      if (!action) return

      if (key === 'filtroData' && value !== OPCOES_FILTRO_DATA.ESPECIFICA) {
        this.$store.dispatch(
          'consolidator/setWalletFiltersLastValidData',
          value
        )
      }

      this.$store.dispatch(action, value)
      this.$store.dispatch('consolidator/setWalletFiltersUserChangedField', {
        key,
        value: true,
      })
    },
    setFieldValue(key, value) {
      if (this.deferApply) {
        this.setDraftValue(key, value)
        return
      }
      this.commitFieldToStore(key, value)
    },
    toSelectSearchItems(field) {
      const items = field.items || []
      const itemText = field.itemText || 'text'
      const itemValue = field.itemValue || 'value'
      const mapped = items
        .filter((item) => item !== null && item !== undefined)
        .map((item) => {
          if (typeof item === 'string') return { id: item, value: item }
          return { id: item[itemValue], value: item[itemText] }
        })
      if (!hasAsyncItems(field)) return mapped
      if (field.multiple) return mapped
      const currentValue = this.getFieldValue(field.key)
      if (
        currentValue &&
        !mapped.some(
          (option) =>
            option.id === currentValue || option.value === currentValue
        )
      ) {
        mapped.unshift({ id: currentValue, value: currentValue })
      }
      return mapped
    },
    getFieldDisplayLabel(field) {
      if (field.key !== 'filtroData') return ''
      return formatDateFilterLabel({
        filtroData: this.getFieldValue(field.key),
        dataInicio: this.getDateValue('dataInicio'),
        dataFim: this.getDateValue('dataFim'),
      })
    },
    openCustomDateModal() {
      const current = this.getFieldValue('filtroData')
      const wasEspecifica = current === OPCOES_FILTRO_DATA.ESPECIFICA
      GlobalBus.$emit(MODAL.CONSOLIDADOR_WALLET_CUSTOM_DATE, {
        initialDataInicio: wasEspecifica
          ? this.getDateValue('dataInicio') || ''
          : '',
        initialDataFim: wasEspecifica ? this.getDateValue('dataFim') || '' : '',
        callback: (values) => this.handleCustomDateApply(values),
        onCancel: () => this.handleCustomDateCancel(),
      })
    },
    handleCustomDateApply({ dataInicio, dataFim }) {
      if (this.deferApply) {
        this.setDraftValue('dataInicio', dataInicio)
        this.setDraftValue('dataFim', dataFim)
        this.setDraftValue('filtroData', OPCOES_FILTRO_DATA.ESPECIFICA)
        return
      }
      this.$store.dispatch(
        'consolidator/setWalletFiltersDataInicio',
        dataInicio
      )
      this.$store.dispatch('consolidator/setWalletFiltersDataFim', dataFim)
      this.$store.dispatch(
        'consolidator/setWalletFiltersData',
        OPCOES_FILTRO_DATA.ESPECIFICA
      )
      this.$store.dispatch(
        'consolidator/setWalletFiltersLastValidData',
        OPCOES_FILTRO_DATA.ESPECIFICA
      )
      this.$store.dispatch('consolidator/setWalletFiltersUserChangedField', {
        key: 'filtroData',
        value: true,
      })
      this.emitFilter()
    },
    handleCustomDateCancel() {
      this.selectRemountKey += 1
    },
    onReselect(field, value) {
      if (this.deferApply) {
        if (
          field.key === 'filtroData' &&
          value === OPCOES_FILTRO_DATA.ESPECIFICA
        ) {
          this.openCustomDateModal()
        }
        return
      }
      this.$store.dispatch('consolidator/setWalletFiltersUserChangedField', {
        key: field.key,
        value: true,
      })
      if (
        field.key === 'filtroData' &&
        value === OPCOES_FILTRO_DATA.ESPECIFICA
      ) {
        this.openCustomDateModal()
        return
      }
      this.emitFilter()
    },
    handleFieldChange(field, value) {
      if (field.multiple) {
        if (this.deferApply) this.handleFieldApply(field, value)
        return
      }
      const current = this.getFieldValue(field.key)
      if (this.valuesAreEqual(current, value)) return
      if (this.shouldOpenCustomDate(field, value)) {
        if (current === OPCOES_FILTRO_DATA.ESPECIFICA) return
        this.openCustomDateModal()
        return
      }
      this.commitFieldChange(field, value)
    },
    shouldOpenCustomDate(field, value) {
      return (
        field.key === 'filtroData' && value === OPCOES_FILTRO_DATA.ESPECIFICA
      )
    },
    commitFieldChange(field, value) {
      const previousModo = this.draftModo
      this.setFieldValue(field.key, value)
      if (field.key === 'modoFiltroCorretora') {
        this.syncGroupFilterToModo(value, previousModo)
      }
      if (field.key === 'filtroData' && !this.deferApply) {
        this.emitFilter()
      }
    },
    syncGroupFilterToModo(modo, previousModo) {
      this.returnGroupSelectionToStandalone(previousModo)
      if (!WALLET_STANDALONE_FILTER_KEYS.includes(modo)) {
        this.resetFiltroCorretoraToDefault()
        return
      }
      const selection = this.getFieldValue(modo)
      const transferred = Array.isArray(selection) ? [...selection] : selection
      this.setFieldValue('filtroCorretora', transferred)
    },
    returnGroupSelectionToStandalone(previousModo) {
      if (!WALLET_STANDALONE_FILTER_KEYS.includes(previousModo)) return
      const selection = this.getFieldValue('filtroCorretora')
      const returned = Array.isArray(selection) ? [...selection] : selection
      this.setFieldValue(previousModo, returned)
    },
    handleFieldApply(field, value) {
      if (!field.multiple) return
      const current = this.getFieldValue(field.key)
      if (this.valuesAreEqual(current, value)) return
      this.setFieldValue(field.key, value)
      if (!this.deferApply) this.emitFilter()
    },
    resetFiltroCorretoraToDefault() {
      const defaultValue = WALLET_FIELD_DEFAULTS.filtroCorretora.default
      if (this.deferApply) {
        this.setDraftValue('filtroCorretora', defaultValue)
        return
      }
      this.$store.dispatch(FIELD_KEY_TO_ACTION.filtroCorretora, defaultValue)
      this.$store.dispatch('consolidator/setWalletFiltersUserChangedField', {
        key: 'filtroCorretora',
        value: false,
      })
    },
    valuesAreEqual(a, b) {
      if (a === b) return true
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false
        return a.every((item, index) => item === b[index])
      }
      return false
    },
    applyDefaults() {
      if (this.deferApply) {
        this.ensureDraftKeys()
        return
      }
      restoreIncompleteSpecificDate({
        fields: this.combinedFields,
        store: this.$store,
      })
      applyWalletFilterDefaults({
        fields: this.combinedFields,
        store: this.$store,
      })
    },
    emitFilter() {
      const payload = {}
      for (const field of this.resolvedFields) {
        payload[field.key] = this.getFieldValue(field.key)
      }
      this.$emit('filter', payload)
    },
  },
}
</script>

<template>
  <div
    class="consolidator-wallet-filter"
    :class="{ 'consolidator-wallet-filter--stacked': stacked }"
  >
    <div
      v-for="field in resolvedFields"
      :key="field.key"
      class="consolidator-wallet-filter__field"
      :class="{
        'consolidator-wallet-filter__field--flex': field.key === 'filtroData',
      }"
    >
      <div class="az-text-label">
        {{ field.label }}
      </div>

      <input-by-type
        ref="fieldInputs"
        :key="`${field.key}-${selectRemountKey}`"
        type="select-search"
        :value="getFieldValue(field.key)"
        :options="toSelectSearchItems(field)"
        :single="!field.multiple"
        :require-selection="!field.multiple"
        preserve-items-order
        :selection-text="getFieldDisplayLabel(field)"
        return-by="id"
        :disabled="disabled"
        :loading="walletAnalysisOptionsFieldsLoading"
        @input="handleFieldChange(field, $event)"
        @apply="handleFieldApply(field, $event)"
        @reselect="onReselect(field, $event)"
      />
    </div>

    <WalletCustomDate />
  </div>
</template>

<style lang="sass" scoped>
.consolidator-wallet-filter
  display: flex
  flex-wrap: wrap
  gap: 8px
  align-items: flex-end

  &__field
    width: 150px !important
    min-width: 150px !important

    &--flex
      width: auto !important
      min-width: 150px !important
      flex: 1 1 auto

  &--stacked
    flex-direction: column
    flex-wrap: nowrap
    gap: 16px
    align-items: stretch

    .consolidator-wallet-filter__field
      width: 100% !important
      min-width: 0 !important

      &--flex
        width: 100% !important
        flex: none
</style>
